// OptionsChain.jsx — light theme, ATM centered, scrollable
import React, { useEffect, useRef } from "react";

function oi(n) {
  if (n == null) return "—";
  if (Math.abs(n) >= 1e7) return (n / 1e7).toFixed(1) + "Cr";
  if (Math.abs(n) >= 1e5) return (n / 1e5).toFixed(1) + "L";
  return n.toLocaleString("en-IN");
}
function ltp(n)          { return n != null ? Number(n).toFixed(1) : "—"; }
function greek(n, dec=2) { return n != null ? Number(n).toFixed(dec) : "—"; }
function chgCls(v)       { return v > 0 ? "text-green-600" : v < 0 ? "text-red-600" : "text-gray-400"; }

export default function OptionsChain({ chain, spot }) {
  const containerRef = useRef(null);
  const atmRowRef    = useRef(null);

  if (!chain || chain.length === 0) return null;

  const sorted = [...chain].sort((a, b) => a.strike - b.strike);

  const atm = spot
    ? sorted.reduce((p, c) => Math.abs(c.strike - spot) < Math.abs(p.strike - spot) ? c : p)
    : sorted.reduce((p, c) =>
        ((c.call_oi || 0) + (c.put_oi || 0)) > ((p.call_oi || 0) + (p.put_oi || 0)) ? c : p
      );

  const hasGreeks = sorted.some(r => r.call_iv != null || r.call_delta != null);
  const hasLtp    = sorted.some(r => r.call_ltp != null || r.put_ltp != null);
  const maxOI     = Math.max(...sorted.map(r => Math.max(r.call_oi || 0, r.put_oi || 0)));

  useEffect(() => {
    if (atmRowRef.current && containerRef.current) {
      const row       = atmRowRef.current;
      const container = containerRef.current;
      const offset    = row.offsetTop - container.offsetTop - container.clientHeight / 2 + row.clientHeight / 2;
      container.scrollTop = offset;
    }
  }, [chain]);

  return (
    <div ref={containerRef} className="h-72 overflow-y-auto overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead className="sticky top-0 bg-white z-10">
          <tr className="text-gray-400 border-b border-gray-200 text-right">
            <th className="pb-1 pr-1 text-left">CALLS</th>
            <th className="pb-1 pr-1">OI</th>
            <th className="pb-1 pr-1">OI Δ</th>
            {hasLtp    && <th className="pb-1 pr-1">LTP</th>}
            {hasGreeks && <th className="pb-1 pr-1">IV%</th>}
            {hasGreeks && <th className="pb-1 pr-1">Δ</th>}
            <th className="pb-1 px-2 text-center font-bold text-gray-600">Strike</th>
            {hasGreeks && <th className="pb-1 pl-1 text-left">Δ</th>}
            {hasGreeks && <th className="pb-1 pl-1 text-left">IV%</th>}
            {hasLtp    && <th className="pb-1 pl-1 text-left">LTP</th>}
            <th className="pb-1 pl-1 text-left">OI Δ</th>
            <th className="pb-1 pl-1 text-left">OI</th>
            <th className="pb-1 text-left">PUTS</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const isAtm = row.strike === atm?.strike;
            const ceBar = maxOI > 0 ? ((row.call_oi || 0) / maxOI) * 100 : 0;
            const peBar = maxOI > 0 ? ((row.put_oi  || 0) / maxOI) * 100 : 0;

            return (
              <tr
                key={row.strike}
                ref={isAtm ? atmRowRef : null}
                className={`border-b transition-colors ${isAtm ? "bg-blue-50 border-blue-200" : "border-gray-100 hover:bg-gray-50"}`}
              >

                <td className="py-1 pr-1 text-left">
                  {isAtm && <span className="text-blue-600 text-xs font-semibold mr-1">ATM</span>}
                </td>
                <td className="pr-1 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-10 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-1 bg-red-400 rounded-full" style={{ width: `${ceBar}%` }} />
                    </div>
                    <span className="text-gray-600 w-10 text-right">{oi(row.call_oi)}</span>
                  </div>
                </td>
                <td className={`pr-1 text-right ${chgCls(row.call_oi_chg)}`}>
                  {row.call_oi_chg != null ? (row.call_oi_chg > 0 ? "+" : "") + oi(row.call_oi_chg) : "—"}
                </td>
                {hasLtp    && <td className="pr-1 text-right text-amber-600">{ltp(row.call_ltp)}</td>}
                {hasGreeks && <td className="pr-1 text-right text-cyan-600">{greek(row.call_iv, 1)}</td>}
                {hasGreeks && <td className="pr-1 text-right text-indigo-500">{greek(row.call_delta)}</td>}
                <td className={`px-2 text-center font-bold ${isAtm ? "text-blue-600 text-sm" : "text-gray-700"}`}>
                  {row.strike}
                </td>
                {hasGreeks && <td className="pl-1 text-left text-indigo-500">{greek(row.put_delta)}</td>}
                {hasGreeks && <td className="pl-1 text-left text-cyan-600">{greek(row.put_iv, 1)}</td>}
                {hasLtp    && <td className="pl-1 text-left text-amber-600">{ltp(row.put_ltp)}</td>}
                <td className={`pl-1 ${chgCls(row.put_oi_chg)}`}>
                  {row.put_oi_chg != null ? (row.put_oi_chg > 0 ? "+" : "") + oi(row.put_oi_chg) : "—"}
                </td>
                <td className="pl-1 text-left">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600 w-10">{oi(row.put_oi)}</span>
                    <div className="w-10 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-1 bg-green-500 rounded-full" style={{ width: `${peBar}%` }} />
                    </div>
                  </div>
                </td>
                <td />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

